import React from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useIdleTimeout, clearIdleMarker } from '../../hooks/useIdleTimeout';

const seconds = ms => Math.max(0, Math.ceil(ms / 1000));

// Wraps the signed-in app. Counts quiet time and signs the user out when it
// runs long, warning first so nobody loses what they were typing without notice.
export default function IdleTimeoutGuard({ children }) {
  const { user, signOut } = useAuth();

  const { msLeft, staySignedIn } = useIdleTimeout({
    enabled: !!user,
    onTimeout: async () => {
      clearIdleMarker();
      await signOut();
    },
  });

  return (
    <>
      {children}
      {msLeft != null && (
        <div style={styles.overlay} role="alertdialog" aria-modal="true"
          aria-labelledby="idle-title" aria-describedby="idle-body">
          <div style={styles.card}>
            <ShieldAlert size={30} style={{ color: 'var(--terracotta)' }} aria-hidden="true" />
            <h2 id="idle-title" style={styles.title}>Still there?</h2>
            <p id="idle-body" style={styles.body}>
              You'll be signed out in <strong>{seconds(msLeft)} seconds</strong> because
              there's been no activity. This keeps your information private if you leave
              your device unattended.
            </p>
            <button className="btn btn-primary" onClick={staySignedIn} autoFocus style={{ width: '100%' }}>
              Keep me signed in
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => { clearIdleMarker(); signOut(); }}
              style={{ width: '100%', marginTop: '0.5rem' }}
            >
              Sign out now
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(var(--navy-rgb), 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '1.25rem',
  },
  card: {
    background: 'var(--white)', borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)', padding: '1.75rem 1.5rem',
    maxWidth: '24rem', width: '100%', textAlign: 'center',
  },
  title: {
    fontFamily: 'var(--font-serif)', fontSize: '1.35rem',
    color: 'var(--navy)', margin: '0.6rem 0 0.5rem',
  },
  body: {
    fontSize: '0.9rem', lineHeight: 1.6,
    color: 'var(--charcoal)', margin: '0 0 1.25rem',
  },
};
